import { useAtomValue, useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Button, Display, Label } from '@growtherapy/sprout-ui';

import { Navbar, PageContainer, PageProviders } from '../../components';
import { useGetTelehealthSessionInfo } from '../../hooks';
import { Theme } from '../../state';
import { useEnterWaitingRoomCallback } from '../nav/useEnterWaitingRoomCallback';
import { isSoftBlockResolvedAtom } from '../softBlockResolvedAtom';
import { visitorNameAtom } from '../state';

export function Page({ onContinue }: { onContinue: () => void }) {
  const t = useTranslate();

  const { providerShortId, patientShortId } = useParams();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);

  const providerName = data?.telehealthSessionInfo.provider.name ?? 'your provider';

  return (
    <PageProviders theme={Theme.DARK}>
      <PageContainer>
        <Navbar showLanguageSwitcher />

        <div className='h-full w-full flex justify-center items-center flex-col  max-w-screen-md text-center m-auto motion-safe:animate-[appear_2s_cubic-bezier(0.16,1,0.3,1)]'>
          <div className='mb-component-150 gap-component-75 flex flex-col items-center justify-center'>
            <Label.Small className='text-text-inverseMuted rebrand:text-text-inverseMuted'>
              {t('telehealth-core:preSessionMeasures.beforeYourSession')}
            </Label.Small>

            <Display.Default className='text-neutral-100 rebrand:text-text-inverse'>
              {t('telehealth-core:preSessionMeasures.helpProviderUnderstandHowYoureDoing', { providerName })}
            </Display.Default>

            <Body.Default className='text-text-inverseMuted rebrand:text-text-inverseMuted max-w-screen-sm'>
              {t('telehealth-core:preSessionMeasures.completeABriefCheckIn')}
            </Body.Default>
          </div>

          <Button
            onClick={onContinue}
            use='primary'
            className='bg-neutral-100 text-neutral-800 rebrand:bg-surface-default rebrand:text-text-default'
          >
            {t('telehealth-core:preSessionMeasures.getStarted')}
          </Button>
        </div>
      </PageContainer>
    </PageProviders>
  );
}

export function PageWrapper() {
  const enterWaitingRoom = useEnterWaitingRoomCallback();
  const setIsSoftBlockResolved = useSetAtom(isSoftBlockResolvedAtom);
  const visitorName = useAtomValue(visitorNameAtom);

  const onContinue = () => {
    // Continuing resolves the soft block so the eligibility check routes the
    // client to the waiting room instead of back into the soft block.
    setIsSoftBlockResolved(true);
    enterWaitingRoom(visitorName);
  };

  return <Page onContinue={onContinue} />;
}
